import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  XCircle,
} from 'lucide-react';
import React, { useState } from 'react';

// -----------------------------------------------------------------------------
// Actions: human_response selector + Fix in chat
// -----------------------------------------------------------------------------
import {
  useFixInChatMutation,
  useUpdateFindingResponseMutation,
} from '@/services/api/proofreadingService';
import {
  ProofreadingFinding,
  ProofreadingFindingType,
  ProofreadingRuleResult,
  ProofreadingRuleResultStatus,
} from '@/services/types/proofreading-types';

import { CitationInlineViewer } from './CitationInlineViewer';

const statusConfig: Record<
  ProofreadingRuleResultStatus,
  {
    icon: React.ReactNode;
    bgColor: string;
    textColor: string;
    label: string;
  }
> = {
  no_issues_found: {
    icon: <CheckCircle className="h-4 w-4" />,
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    label: 'Correct',
  },
  minor_issues_found: {
    icon: <AlertTriangle className="h-4 w-4" />,
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    label: 'Error',
  },
  moderate_issues_found: {
    icon: <AlertTriangle className="h-4 w-4" />,
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    label: 'Error',
  },
  critical_issues_found: {
    icon: <XCircle className="h-4 w-4" />,
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    label: 'Error',
  },
  unable_to_assess: {
    icon: <AlertCircle className="h-4 w-4" />,
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    label: 'Please Verify',
  },
  processing: {
    icon: <Clock className="h-4 w-4 animate-pulse" />,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    label: 'Processing',
  },
  failed: {
    icon: <XCircle className="h-4 w-4" />,
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    label: 'Failed',
  },
};

// -----------------------------------------------------------------------------
// Shared sub-component to render Issues / Evidence lists
// -----------------------------------------------------------------------------

interface FindingListProps {
  title: string;
  icon: React.ReactNode;
  color: 'red' | 'green';
  findings: ProofreadingFinding[];
  matterId: string;
  resultId?: string;
  workspaceId?: string;
  jobId?: string;
  showActions?: boolean;
  activeFindingId: string | undefined;
  setActiveFindingId: (id: string | undefined) => void;
  activeCitationId: string | undefined;
  setActiveCitationId: (id: string | undefined) => void;
}

const FindingList: React.FC<FindingListProps> = ({
  title,
  icon,
  color,
  findings,
  matterId,
  resultId,
  workspaceId,
  jobId,
  showActions = true,
  activeFindingId,
  setActiveFindingId,
  activeCitationId,
  setActiveCitationId,
}) => {
  if (!findings.length) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h4
          className={`font-semibold ${
            color === 'red' ? 'text-red-700' : 'text-green-700'
          }`}
        >
          {title} ({findings.length})
        </h4>
      </div>
      <div className="space-y-2">
        {findings.map((f) => (
          <div
            key={f.id}
            className={`bg-white rounded-lg p-3 border space-y-2 transition-all duration-300 ${
              activeFindingId === f.id
                ? 'border-blue-300 shadow-md ring-2 ring-blue-200'
                : 'border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                {f.section_name && (
                  <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Section: {f.section_name}
                  </div>
                )}
                <p className="text-sm text-gray-800 leading-relaxed">
                  {f.description}
                </p>
              </div>
              {showActions && workspaceId && jobId && (
                <FindingActions
                  matterId={matterId}
                  workspaceId={workspaceId}
                  jobId={jobId}
                  findingId={f.id}
                  currentResponse={f.human_response}
                />
              )}
            </div>
            {f.citation_ids?.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setActiveFindingId(f.id);
                  setActiveCitationId(f.citation_ids[0]);
                }}
                className="inline-flex items-center text-xs font-medium text-blue-600 hover:underline"
              >
                View citations
              </button>
            )}

            {activeFindingId === f.id && (
              <CitationInlineViewer
                matterId={matterId}
                citationIds={f.citation_ids}
                activeCitationId={activeCitationId}
                setActiveCitationId={setActiveCitationId}
                setActiveFindingId={setActiveFindingId}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Main Card
// -----------------------------------------------------------------------------

interface ProofreadingRuleResultCardProps {
  result: ProofreadingRuleResult;
  matterId: string;
  workspaceId?: string;
  jobId?: string;
}

export const ProofreadingRuleResultCard: React.FC<
  ProofreadingRuleResultCardProps
> = ({ result, matterId, workspaceId, jobId }) => {
  const [activeFindingId, setActiveFindingId] = useState<string | undefined>();
  const [activeCitationId, setActiveCitationId] = useState<
    string | undefined
  >();
  const [expanded, setExpanded] = useState(false);

  const issues = result.findings.filter(
    (f) => f.finding_type === ProofreadingFindingType.ISSUE,
  );
  const evidences = result.findings.filter(
    (f) => f.finding_type === ProofreadingFindingType.EVIDENCE,
  );

  const config = statusConfig[result.status];
  const hasFindings = issues.length > 0 || evidences.length > 0;

  return (
    <div
      className={`rounded-lg border ${config.bgColor} transition-all duration-200`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className={config.textColor}>{config.icon}</div>
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {result.title}
              </h3>
            </div>
            {(result.reason || hasFindings) && (
              <div className="flex items-start justify-between gap-2 text-sm leading-relaxed text-gray-600">
                {result.reason && (
                  <p className={`flex-1 text-sm ${expanded ? '' : 'truncate'}`}>
                    {result.reason}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${config.bgColor} ${config.textColor} ring-1 ring-inset`}
            >
              {config.icon}
              {config.label}
            </span>

            {hasFindings && (
              <button
                type="button"
                className={`inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap ${config.textColor} hover:underline transition-colors`}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                {expanded
                  ? 'Hide findings'
                  : `Show findings (${issues.length + evidences.length})`}
              </button>
            )}
          </div>
        </div>

        {expanded && hasFindings && (
          <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
            <FindingList
              title="Issues Found"
              icon={<XCircle className="h-4 w-4 text-red-600" />}
              color="red"
              findings={issues}
              matterId={matterId}
              resultId={result.id}
              workspaceId={workspaceId}
              jobId={jobId}
              showActions
              activeFindingId={activeFindingId}
              setActiveFindingId={setActiveFindingId}
              activeCitationId={activeCitationId}
              setActiveCitationId={setActiveCitationId}
            />

            <FindingList
              title="Supporting Evidence"
              icon={<CheckCircle className="h-4 w-4 text-green-600" />}
              color="green"
              findings={evidences}
              matterId={matterId}
              resultId={result.id}
              workspaceId={workspaceId}
              jobId={jobId}
              showActions={false}
              activeFindingId={activeFindingId}
              setActiveFindingId={setActiveFindingId}
              activeCitationId={activeCitationId}
              setActiveCitationId={setActiveCitationId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const FindingActions: React.FC<{
  matterId: string;
  workspaceId: string;
  jobId: string;
  findingId: string;
  currentResponse?: 'revised' | 'not_applicable' | 'incorrect' | null;
}> = ({ matterId, workspaceId, jobId, findingId, currentResponse }) => {
  const [updateFindingResponse, { isLoading: isUpdating }] =
    useUpdateFindingResponseMutation();
  const [fixInChat, { isLoading: isFixing }] = useFixInChatMutation();

  // Local UI state so the select updates immediately (optimistic UI)
  const [value, setValue] = useState<
    '' | 'revised' | 'not_applicable' | 'incorrect'
  >('');

  const onSet = async (
    val: '' | 'revised' | 'not_applicable' | 'incorrect',
  ) => {
    if (val === '') {
      setValue('');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('proofreading:response-updated'));
      }
      try {
        await updateFindingResponse({
          matterId,
          workspaceId,
          jobId,
          findingId,
          human_response: null,
        }).unwrap();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('proofreading:response-updated'),
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to clear finding response', e);
      }
      return;
    }
    setValue(val);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('proofreading:response-updated'));
    }
    try {
      await updateFindingResponse({
        matterId,
        workspaceId,
        jobId,
        findingId,
        human_response: val,
      }).unwrap();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('proofreading:response-updated'));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to update finding response', e);
    }
  };

  const onFix = async () => {
    try {
      const res = await fixInChat({
        matterId,
        workspaceId,
        findingId,
      }).unwrap();
      // Notify a global event bus if present
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('agent-chat:navigate-thread', {
            detail: { threadId: res.thread_id },
          }),
        );
      }
    } catch {
      // no-op
    }
  };

  return (
    <div className="flex items-center gap-2">
      <select
        className="text-xs border rounded px-2 py-1"
        value={value || (currentResponse ?? '')}
        onChange={(e) =>
          onSet(e.target.value as 'revised' | 'not_applicable' | 'incorrect')
        }
        disabled={isUpdating}
      >
        <option value="">Clear / Unmark</option>
        <option value="revised">Revised</option>
        <option value="not_applicable">Not applicable</option>
        <option value="incorrect">Incorrect</option>
      </select>
      <button
        type="button"
        className="text-xs text-blue-600 hover:underline"
        onClick={onFix}
        disabled={isFixing}
      >
        Fix in chat
      </button>
    </div>
  );
};
